import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast, confirmDialog } from "@/lib/ui";
import { Bell, Check, Heart, Sparkles, MessageCircle, Camera, ShieldCheck, Users } from "lucide-react";

const ICONS = {
  interest_received:      <Heart className="h-4 w-4 text-rose-500" />,
  intro_received:         <Heart className="h-4 w-4 text-rose-500" />,
  new_match:              <Sparkles className="h-4 w-4 text-gold-500" />,
  new_message:            <MessageCircle className="h-4 w-4 text-olive-600" />,
  photo_access_request:   <Camera className="h-4 w-4 text-brown-600" />,
  photo_access_granted:   <Camera className="h-4 w-4 text-olive-600" />,
  photo_access_declined:  <Camera className="h-4 w-4 text-brown-400" />,
  verification_decision:  <ShieldCheck className="h-4 w-4 text-sky-600" />,
  wali_invite_accepted:   <Users className="h-4 w-4 text-olive-700" />,
};

function linkFor(n) {
  const t = n.ref_type;
  const id = n.ref_id;
  if (t === "conversation" && id)   return `/chat?open=${id}`;
  if (t === "profile" && id)         return `/matches/${id}`;
  if (t === "interest")              return "/interests";
  if (t === "match" && id)           return `/chat?match=${id}`;
  if (t === "photo_access")          return "/photo-requests";
  if (t === "verification")          return "/verify";
  if (t === "family_invite")         return "/family";
  if (t === "boost")                 return "/pricing";
  return "/notifications";
}

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=50");
      setItems(data.items || []);
    } catch (e) { setErr(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    try { await api.post("/notifications/read"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const markOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); await load(); } catch {}
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-7" data-testid="notifications-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow flex items-center gap-2"><Bell className="h-3 w-3" /> Indbakke</div>
          <h1 className="mt-2 font-serif text-4xl text-olive-700">Notifikationer</h1>
        </div>
        <button onClick={markAllRead} className="btn-secondary text-sm" data-testid="mark-all-read"><Check className="h-4 w-4" /> Markér alle som læst</button>
      </div>

      {err && <div className="mt-4 text-destructive text-sm">{err}</div>}

      {items.length === 0 ? (
        <div className="mt-10 card-soft p-10 text-center text-brown-500" data-testid="notifications-empty">
          Ingen notifikationer endnu — vi giver dig besked når noget sker.
        </div>
      ) : (
        <ul className="mt-8 space-y-2">
          {items.map((n) => (
            <li key={n.id} className={`card-soft p-4 flex items-start gap-3 ${!n.read ? "border-olive-500/40 bg-olive-50/50" : ""}`} data-testid={`notif-${n.id}`}>
              <div className="mt-0.5">{ICONS[n.type] || <Bell className="h-4 w-4 text-brown-500" />}</div>
              <div className="flex-1 min-w-0">
                <Link to={linkFor(n)} onClick={() => markOne(n.id)} className="block">
                  <div className={`text-sm ${!n.read ? "font-medium text-olive-700" : "text-brown-700"}`}>{n.title}</div>
                  {n.body && <div className="text-xs text-brown-500 mt-0.5 line-clamp-2">{n.body}</div>}
                  <div className="text-[10px] text-brown-400 mt-1">{new Date(n.created_at).toLocaleString("da-DK")}</div>
                </Link>
              </div>
              {!n.read && <span className="mt-1 h-2 w-2 rounded-full bg-olive-500 shrink-0" aria-label="uread" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
